export const formatTime = (timeInMs) => {
  if (!timeInMs) return '-';
  return (timeInMs / 1000).toFixed(2) + 's';
};

export const calculateStats = (times) => {
  if (!times || times.length === 0) return {};

  const sortedTimes = [...times].sort((a, b) => a - b);
  const sum = sortedTimes.reduce((acc, time) => acc + time, 0);
  
  return {
    best: sortedTimes[0],
    worst: sortedTimes[sortedTimes.length - 1],
    average: sum / sortedTimes.length,
    median: sortedTimes[Math.floor(sortedTimes.length / 2)],
    count: sortedTimes.length,
    rollingAverage5: calculateRollingAverage(times, 5),
    rollingAverage12: calculateRollingAverage(times, 12)
  };
};

export const calculateRollingAverage = (times, windowSize) => {
  if (!times || times.length < windowSize) return null;
  
  const lastNTimes = times.slice(-windowSize);
  if (windowSize === 5) {
    // For ao5, exclude best and worst times
    const sorted = [...lastNTimes].sort((a, b) => a - b);
    const middleThree = sorted.slice(1, -1);
    return Math.round(middleThree.reduce((sum, time) => sum + time, 0) / 3);
  } else {
    // Current writers still store this untrimmed mean for ao12.
    // Canonical averages use trimmedRollingAverage.
    return Math.round(lastNTimes.reduce((sum, time) => sum + time, 0) / windowSize);
  }
};

export const calculateAverages = (times) => {
  if (!times || times.length === 0) {
    return { ao5: null, ao12: null, fastest: null };
  }

  return {
    ao5: calculateRollingAverage(times, 5),
    ao12: calculateRollingAverage(times, 12),
    fastest: Math.min(...times)
  };
};

export const prepareChartData = (times) => {
  return {
    labels: times.map((_, index) => `Solve ${index + 1}`),
    datasets: [{
      label: 'Time (seconds)',
      data: times.map(time => time / 1000),
      borderColor: 'rgb(75, 192, 192)',
      tension: 0.1
    }]
  };
};

export const SOLVE_TIME_MAX_MS = 86400000;

export const isValidSolveTime = (time) => (
  typeof time === 'number'
  && Number.isFinite(time)
  && time > 0
  && time <= SOLVE_TIME_MAX_MS
);

export const compareSolveEvents = (left, right) => {
  if (left.timestamp < right.timestamp) return -1;
  if (left.timestamp > right.timestamp) return 1;
  const leftId = left.id ?? '';
  const rightId = right.id ?? '';
  if (leftId < rightId) return -1;
  if (leftId > rightId) return 1;
  return 0;
};

const orderedValidTimes = (events, include) => events
  .filter((event) => include(event) && isValidSolveTime(event.time))
  .slice()
  .sort(compareSolveEvents)
  .map((event) => event.time);

export const timesForCaseMode = (events, algorithmId, mode) => orderedValidTimes(
  events,
  (event) => event.algorithmId === algorithmId && event.mode === mode
);

export const timesForGlobalMode = (events, mode) => orderedValidTimes(
  events,
  (event) => event.mode === mode
);

export const trimmedRollingAverage = (times, windowSize) => {
  if (!times || !Number.isInteger(windowSize) || windowSize < 1 || times.length < windowSize) {
    return null;
  }

  const window = times.slice(-windowSize);
  if (window.some((time) => !isValidSolveTime(time))) return null;

  const trimCount = Math.ceil(windowSize * 0.05);
  const remaining = windowSize - (trimCount * 2);
  if (remaining < 1) return null;

  const sorted = [...window].sort((left, right) => left - right);
  const middle = sorted.slice(trimCount, sorted.length - trimCount);
  const sum = middle.reduce((total, time) => total + time, 0);
  return Math.round(sum / remaining);
};

export const bestRollingAverage = (times, windowSize) => {
  if (!times || times.length < windowSize || times.some((time) => !isValidSolveTime(time))) {
    return null;
  }

  let best = null;
  for (let end = windowSize; end <= times.length; end += 1) {
    const average = trimmedRollingAverage(times.slice(end - windowSize, end), windowSize);
    if (average === null || !Number.isFinite(average)) return null;
    if (best === null || average < best) best = average;
  }
  return best;
};

export const solveAverages = (times) => {
  const list = times ?? [];
  if (list.some((time) => !isValidSolveTime(time))) return null;

  return {
    attempts: list.length,
    fastest: list.length === 0 ? null : Math.min(...list),
    last: list.length === 0 ? null : list[list.length - 1],
    ao5: trimmedRollingAverage(list, 5),
    ao12: trimmedRollingAverage(list, 12),
    bestAo5: bestRollingAverage(list, 5),
    bestAo12: bestRollingAverage(list, 12),
  };
};

export const displayedSolveStats = (times) => {
  const averages = solveAverages(times);
  if (!averages) {
    return {
      totalAttempts: null,
      fastestTime: null,
      lastTime: null,
      rollingAverage5: null,
      rollingAverage12: null,
      bestAo5: null,
      bestAo12: null,
      times: times ?? [],
    };
  }

  return {
    totalAttempts: averages.attempts,
    fastestTime: averages.fastest,
    lastTime: averages.last,
    rollingAverage5: averages.ao5,
    rollingAverage12: averages.ao12,
    bestAo5: averages.bestAo5,
    bestAo12: averages.bestAo12,
    times: times ?? [],
  };
};

const statsFromTimes = (times) => {
  if (!times || times.length === 0) return null;
  const displayed = displayedSolveStats(times);
  if (!displayed.totalAttempts) return null;
  return displayed;
};

export const deriveCaseStats = (events, algorithmId, mode) => (
  statsFromTimes(timesForCaseMode(events, algorithmId, mode))
);

export const deriveGlobalStats = (events, mode) => (
  statsFromTimes(timesForGlobalMode(events, mode))
);

const lastEventByCase = (events, mode) => {
  const latest = new Map();
  events
    .filter((event) => (
      event.mode === mode
      && event.algorithmId
      && isValidSolveTime(event.time)
    ))
    .slice()
    .sort(compareSolveEvents)
    .forEach((event) => {
      latest.set(event.algorithmId, event);
    });
  return latest;
};

const rankRecentCases = (events, mode, latest, cases) => {
  const names = new Map(cases.map((algorithm) => [algorithm.id, algorithm.name]));
  return [...latest.values()]
    .sort((left, right) => {
      if (left.time !== right.time) return right.time - left.time;
      if (left.algorithmId < right.algorithmId) return -1;
      if (left.algorithmId > right.algorithmId) return 1;
      return 0;
    })
    .slice(0, 5)
    .map((event) => {
      const stats = displayedSolveStats(timesForCaseMode(events, event.algorithmId, mode));
      return {
        id: event.algorithmId,
        name: names.get(event.algorithmId) || event.algorithmId,
        totalAttempts: stats.totalAttempts,
        lastTime: stats.lastTime,
        fastestTime: stats.fastestTime,
        rollingAverage5: stats.rollingAverage5,
        rollingAverage12: stats.rollingAverage12,
      };
    });
};

export const recentCasesForMode = (events, mode, cases = []) => (
  rankRecentCases(events, mode, lastEventByCase(events, mode), cases)
);

const missingFromLatest = (latest, cases) => cases
  .filter((algorithm) => !latest.has(algorithm.id))
  .map((algorithm) => ({ id: algorithm.id, name: algorithm.name }));

export const missingCasesForMode = (events, mode, cases = []) => (
  missingFromLatest(lastEventByCase(events, mode), cases)
);

export const derivePracticeAnalysis = (events, casesByMode) => {
  const recent = {};
  const missing = {};
  Object.keys(casesByMode).forEach((mode) => {
    const cases = casesByMode[mode] || [];
    const latest = lastEventByCase(events, mode);
    recent[mode] = rankRecentCases(events, mode, latest, cases);
    missing[mode] = missingFromLatest(latest, cases);
  });
  return { recent, missing };
};

export const chartOptions = {
  responsive: true,
  plugins: {
    title: {
      display: true,
      text: 'Solve Times'
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      title: {
        display: true,
        text: 'Time (seconds)'
      }
    }
  }
}; 